'use client';

import { useEffect } from 'react';
import { useTranslations } from 'next-intl';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Form } from '@/components/ui/form';
import { FormInput } from '@/components/forms/form-input';
import { FormSelect } from '@/components/forms/form-select';
import type { OfficeBiLabel } from '@/features/configurations';

const officeSchema = z.object({
  ar: z.string().min(1),
  en: z.string(),
  status: z.enum(['active', 'inactive'])
});

type OfficeFormValues = z.infer<typeof officeSchema>;

interface LabsFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** null = add mode */
  office: OfficeBiLabel | null;
  /** Arabic names already in use (for duplicate detection) */
  existingAr: string[];
  onSave: (office: OfficeBiLabel) => void;
}

export function LabsFormDialog({
  open,
  onOpenChange,
  office,
  existingAr,
  onSave
}: LabsFormDialogProps) {
  const t = useTranslations('configurations');
  const tCommon = useTranslations('common');

  const form = useForm<OfficeFormValues>({
    resolver: zodResolver(officeSchema),
    defaultValues: { ar: '', en: '', status: 'active' }
  });

  useEffect(() => {
    if (open) {
      form.reset(
        office
          ? { ar: office.ar, en: office.en, status: office.status }
          : { ar: '', en: '', status: 'active' }
      );
    }
  }, [open, office, form]);

  function handleSubmit(values: OfficeFormValues) {
    const ar = values.ar.trim();
    const others = office
      ? existingAr.filter((a) => a !== office.ar)
      : existingAr;
    if (others.includes(ar)) {
      form.setError('ar', { message: t('duplicateOfficeError') });
      return;
    }
    onSave({ ar, en: values.en.trim(), status: values.status });
    onOpenChange(false);
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className='sm:max-w-sm'>
        <DialogHeader>
          <DialogTitle>{office ? t('editOffice') : t('addOffice')}</DialogTitle>
        </DialogHeader>
        <Form
          form={form}
          onSubmit={form.handleSubmit(handleSubmit)}
          className='space-y-3'
        >
          <FormInput
            control={form.control}
            name='ar'
            label={t('arabicName')}
            placeholder={t('arabicNamePlaceholder')}
            required
          />
          <FormInput
            control={form.control}
            name='en'
            label={t('englishName')}
            placeholder={t('englishNamePlaceholder')}
          />
          <FormSelect
            control={form.control}
            name='status'
            options={[
              { label: t('statusActive'), value: 'active' },
              { label: t('statusInactive'), value: 'inactive' }
            ]}
          />
          <DialogFooter>
            <Button
              type='button'
              variant='outline'
              onClick={() => onOpenChange(false)}
            >
              {tCommon('cancel')}
            </Button>
            <Button type='submit'>
              {office ? tCommon('save') : t('addOffice')}
            </Button>
          </DialogFooter>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
